import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, ChevronUp, ChevronDown, X, FolderOpen, Camera } from "lucide-react";
import { productsAPI, uploadAPI, statsAPI, API_ORIGIN } from "../../api";
import { useAuth } from "../../context/AuthContext";

const EditProduct = () => {
  const { productId } = useParams();
  const { isBreeder } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    stock: "",
    unit: "kg",
    product_type: "chicken",
    category: "",
    image_url: "",
    is_live: false,
    lot_size: "",
    lot_price: "",
    available_from: "",
    is_available: true,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [images, setImages] = useState([]); // Liste des images avec {url, preview, id?}
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [commissionRates, setCommissionRates] = useState({
    total_commission_rate: 0.13, // Valeur par défaut (10% + 3%)
    total_commission_percentage: 13,
    seller_rate: 0.87,
    seller_percentage: 87
  });
  const [commissionRatesUnavailable, setCommissionRatesUnavailable] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Charger les taux de commission depuis l'API
  useEffect(() => {
    const loadCommissionRates = async () => {
      try {
        const response = await statsAPI.getCommissionRates();
        if (response.data) {
          setCommissionRates({
            total_commission_rate: response.data.total_commission_rate,
            total_commission_percentage: response.data.total_commission_percentage,
            seller_rate: response.data.seller_rate,
            seller_percentage: response.data.seller_percentage
          });
        }
      } catch (error) {
        console.error("Erreur lors du chargement des taux de commission:", error);
        // On garde les valeurs par défaut en cas d'erreur, mais on prévient
        // le vendeur qu'elles peuvent ne plus être exactes.
        setCommissionRatesUnavailable(true);
      }
    };
    loadCommissionRates();
  }, []);

  // Nettoyer le stream de la caméra quand le composant se démonte
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await productsAPI.getOne(productId);
      const product = response.data;
      
      // Convertir available_from en format datetime-local
      let availableFrom = "";
      if (product.available_from) {
        const date = new Date(product.available_from);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        availableFrom = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      setFormData({
        name: product.name || "",
        description: product.description || "",
        price: product.price?.toString() || "",
        stock: product.stock?.toString() || "",
        unit: product.unit || "kg",
        product_type: product.product_type || "chicken",
        category: product.category || "",
        image_url: product.image_url || "",
        is_live: product.is_live || false,
        lot_size: product.lot_size?.toString() || "",
        lot_price: product.lot_price?.toString() || "",
        available_from: availableFrom,
        is_available: product.is_available !== undefined ? product.is_available : true,
      });
      
      // Charger les images du produit
      if (product.images && product.images.length > 0) {
        setImages(product.images.map(img => ({ url: img.image_url, preview: img.image_url, id: img.id })));
      } else if (product.image_url) {
        // Rétrocompatibilité avec l'ancien système
        setImages([{ url: product.image_url, preview: product.image_url }]);
      } else {
        setImages([]);
      }
    } catch (err) {
      setError("Erreur lors du chargement du produit");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (isBreeder) {
      loadProduct();
    }
  }, [isBreeder, loadProduct]);

  if (!isBreeder) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être éleveur pour modifier un produit
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setError("");
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Vérifier chaque fichier
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        setError("Veuillez sélectionner uniquement des fichiers image");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Une image est trop volumineuse (max 5MB)");
        return;
      }
    }

    setUploadingImage(true);
    setError("");

    // Uploader toutes les images
    const newImages = [];
    for (const file of files) {
      try {
        // Afficher la prévisualisation
        const reader = new FileReader();
        const previewPromise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        const preview = await previewPromise;

        // Uploader l'image
        const response = await uploadAPI.uploadImage(file);
        const imageUrl = `${API_ORIGIN}${response.data.url}`;
        
        newImages.push({ url: imageUrl, preview });
      } catch (err) {
        setError(err.response?.data?.detail || "Erreur lors de l'upload d'une image");
        setUploadingImage(false);
        return;
      }
    }

    setImages([...images, ...newImages]);
    setUploadingImage(false);
    
    // Réinitialiser les inputs
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleTakePhoto = async () => {
    try {
      // Demander l'accès à la caméra
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment' // Caméra arrière sur mobile
        } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      // Attendre que la vidéo soit prête
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error("Erreur d'accès à la caméra:", err);
      setError("Impossible d'accéder à la caméra. Vérifiez les permissions ou utilisez le bouton 'Charger une image'.");
      // Fallback vers l'input file
      cameraInputRef.current?.click();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Définir la taille du canvas
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Dessiner l'image de la vidéo sur le canvas
    context.drawImage(video, 0, 0);

    // Convertir en blob
    canvas.toBlob(async (blob) => {
      if (!blob) return;

      // Créer un fichier à partir du blob
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });

      // Afficher la prévisualisation
      const reader = new FileReader();
      reader.onloadend = () => {
        const preview = reader.result;
        
        // Uploader l'image
        setUploadingImage(true);
        setError("");
        
        uploadAPI.uploadImage(file)
          .then((response) => {
            const imageUrl = `${API_ORIGIN}${response.data.url}`;
            setImages([...images, { url: imageUrl, preview }]);
            setUploadingImage(false);
          })
          .catch((err) => {
            setError(err.response?.data?.detail || "Erreur lors de l'upload de l'image");
            setUploadingImage(false);
          });
      };
      reader.readAsDataURL(blob);

      // Fermer la caméra
      closeCamera();
    }, 'image/jpeg', 0.9);
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleMoveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newImages.length) {
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      setImages(newImages);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      const productData = {
        name: formData.name,
        description: formData.description || null,
        // Le backend exige price > 0 même pour un produit vendu au lot (la
        // colonne est NOT NULL) : on dérive le prix unitaire à partir du
        // prix/nombre d'éléments du lot dans ce cas.
        price: formData.unit === "lot"
          ? parseFloat(formData.lot_price) / parseInt(formData.lot_size, 10)
          : parseFloat(formData.price),
        stock: parseInt(formData.stock, 10),
        unit: formData.unit,
        product_type: formData.product_type,
        category: formData.category || null,
        image_url: images.length > 0 ? images[0].url : null, // Image principale pour rétrocompatibilité
        image_urls: images.map(img => img.url), // Liste de toutes les images
        is_live: formData.is_live,
        lot_size: formData.unit === "lot" && formData.lot_size ? parseInt(formData.lot_size, 10) : null,
        lot_price: formData.unit === "lot" && formData.lot_price ? parseFloat(formData.lot_price) : null,
        available_from: formData.available_from ? new Date(formData.available_from).toISOString() : null,
        is_available: formData.is_available,
      };
      
      await productsAPI.update(productId, productData);
      setSuccess(true);
      setTimeout(() => {
        navigate("/my-products");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la modification du produit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container main-content">
      <div className="page-header">
        <Link to="/my-products" className="back-link icon-inline">
          <ArrowLeft size={16} strokeWidth={2.25} /> Retour à mes produits
        </Link>
        <h1>Modifier le produit</h1>
        <p>Mettez à jour les informations de votre produit</p>
      </div>

      <div className="card" style={{ maxWidth: "600px", margin: "0 auto" }}>
        {success && (
          <div className="alert alert-success">
            Produit modifié avec succès ! Redirection...
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Nom du produit *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Ex: Poulet fermier Label Rouge, Œufs bio, Canard..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="product_type" className="form-label">
              Type de produit *
            </label>
            <select
              id="product_type"
              name="product_type"
              className="form-control"
              value={formData.product_type}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  product_type: e.target.value,
                  // Réinitialiser is_live si on change vers un type qui ne supporte pas les animaux vivants
                  is_live: (e.target.value === "chicken" || e.target.value === "poultry") ? formData.is_live : false,
                  // Adapter l'unité selon le type
                  unit: e.target.value === "eggs" ? "pièce" : e.target.value === "chicken" ? formData.unit : "kg",
                });
              }}
            >
              <option value="chicken">🐔 Poulet</option>
              <option value="eggs">🥚 Œufs</option>
              <option value="poultry">🦆 Autre volaille (canard, dinde, etc.)</option>
              <option value="other">🐷 Autres produits d'élevage</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Décrivez votre produit..."
            />
          </div>

          {(formData.product_type === "chicken" || formData.product_type === "poultry") && (
            <div className="form-group">
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input
                  type="checkbox"
                  id="is_live"
                  name="is_live"
                  checked={formData.is_live}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_live: e.target.checked,
                      unit: e.target.checked && formData.product_type === "chicken" ? "poulet" : formData.unit === "poulet" ? "kg" : formData.unit,
                    })
                  }
                />
                <span>🐔 Animal vivant</span>
              </label>
              <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                Cochez cette case si vous vendez des animaux vivants
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="category" className="form-label">
              Catégorie
            </label>
            <input
              type="text"
              id="category"
              name="category"
              className="form-control"
              value={formData.category}
              onChange={handleChange}
              placeholder="Ex: Poulet fermier, Bio, Label Rouge..."
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "1rem" }}>
            <div className="form-group">
              <label htmlFor="price" className="form-label">
                Prix * (F CFA)
              </label>
              <input
                type="number"
                id="price"
                name="price"
                className="form-control"
                value={formData.price}
                onChange={handleChange}
                required={formData.unit !== "lot"}
                step="1"
                min="0"
                placeholder="5000"
              />
              {formData.price && parseFloat(formData.price) > 0 && (
                <div style={{ 
                  marginTop: "0.5rem", 
                  padding: "0.75rem", 
                  background: "rgba(255, 193, 7, 0.15)", 
                  borderRadius: "var(--radius-sm)", 
                  borderLeft: "3px solid #ffc107",
                  fontSize: "0.85rem"
                }}>
                  <strong style={{ color: "#856404" }}><AlertTriangle size={14} strokeWidth={2.25} style={{display:"inline", verticalAlign:"-2px", marginRight:"0.3rem"}} />Commission PouletDirect :</strong>
                  <div style={{ marginTop: "0.25rem", color: "#856404" }}>
                    Prix affiché : <strong>{parseFloat(formData.price).toLocaleString('fr-FR')} FCFA</strong>
                    <br />
                    Commission PouletDirect ({commissionRates.total_commission_percentage}%) : <strong>{(parseFloat(formData.price) * commissionRates.total_commission_rate).toLocaleString('fr-FR')} FCFA</strong>
                    <br />
                    <span style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#155724" }}>
                      Vous recevrez : <strong>{(parseFloat(formData.price) * commissionRates.seller_rate).toLocaleString('fr-FR')} FCFA</strong>
                    </span>
                  </div>
                  <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.8rem", color: "#856404" }}>
                    PouletDirect prélève une commission de {commissionRates.total_commission_percentage}% par transaction. Tenez-en compte lors de la fixation de votre prix.
                  </p>
                  {commissionRatesUnavailable && (
                    <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.8rem", color: "#856404", fontWeight: 600 }}>
                      Taux non confirmés auprès du serveur — ce pourcentage peut ne plus être exact. Rechargez la page avant de vous fier à ces montants.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="unit" className="form-label">
                Unité
              </label>
              <select
                id="unit"
                name="unit"
                className="form-control"
                value={formData.unit}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    unit: e.target.value,
                    lot_size: e.target.value === "lot" ? formData.lot_size : "",
                    lot_price: e.target.value === "lot" ? formData.lot_price : "",
                  });
                }}
              >
                {formData.product_type === "eggs" ? (
                  <>
                    <option value="pièce">pièce</option>
                    <option value="lot">lot</option>
                  </>
                ) : formData.product_type === "chicken" ? (
                  <>
                    <option value="kg">kg</option>
                    <option value="pièce">pièce</option>
                    <option value="poulet">poulet</option>
                    <option value="lot">lot</option>
                  </>
                ) : (
                  <>
                    <option value="kg">kg</option>
                    <option value="pièce">pièce</option>
                    <option value="lot">lot</option>
                  </>
                )}
              </select>
            </div>
          </div>

          {formData.unit === "lot" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem", padding: "1rem", background: "var(--background)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-light)" }}>
              <div className="form-group">
                <label htmlFor="lot_size" className="form-label">
                  Nombre d'éléments par lot *
                </label>
                <input
                  type="number"
                  id="lot_size"
                  name="lot_size"
                  className="form-control"
                  value={formData.lot_size}
                  onChange={handleChange}
                  required={formData.unit === "lot"}
                  min="1"
                  placeholder="10"
                />
              </div>

              <div className="form-group">
                <label htmlFor="lot_price" className="form-label">
                  Prix du lot * (F CFA)
                </label>
                <input
                  type="number"
                  id="lot_price"
                  name="lot_price"
                  className="form-control"
                  value={formData.lot_price}
                  onChange={handleChange}
                  required={formData.unit === "lot"}
                  step="1"
                  min="0"
                  placeholder="45000"
                />
                {formData.lot_price && parseFloat(formData.lot_price) > 0 && (
                  <div style={{ 
                    marginTop: "0.5rem", 
                    padding: "0.75rem", 
                    background: "rgba(255, 193, 7, 0.15)", 
                    borderRadius: "var(--radius-sm)", 
                    borderLeft: "3px solid #ffc107",
                    fontSize: "0.85rem"
                  }}>
                    <strong style={{ color: "#856404" }}><AlertTriangle size={14} strokeWidth={2.25} style={{display:"inline", verticalAlign:"-2px", marginRight:"0.3rem"}} />Commission PouletDirect :</strong>
                    <div style={{ marginTop: "0.25rem", color: "#856404" }}>
                      Prix affiché : <strong>{parseFloat(formData.lot_price).toLocaleString('fr-FR')} FCFA</strong>
                      <br />
                      Commission PouletDirect ({commissionRates.total_commission_percentage}%) : <strong>{(parseFloat(formData.lot_price) * commissionRates.total_commission_rate).toLocaleString('fr-FR')} FCFA</strong>
                      <br />
                      <span style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#155724" }}>
                        Vous recevrez : <strong>{(parseFloat(formData.lot_price) * commissionRates.seller_rate).toLocaleString('fr-FR')} FCFA</strong>
                      </span>
                    </div>
                    <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.8rem", color: "#856404" }}>
                      PouletDirect prélève une commission de {commissionRates.total_commission_percentage}% par transaction. Tenez-en compte lors de la fixation de votre prix.
                    </p>
                    {commissionRatesUnavailable && (
                      <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.8rem", color: "#856404", fontWeight: 600 }}>
                        Taux non confirmés auprès du serveur — ce pourcentage peut ne plus être exact. Rechargez la page avant de vous fier à ces montants.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="stock" className="form-label">
              Stock disponible * ({formData.unit})
            </label>
            <input
              type="number"
              id="stock"
              name="stock"
              className="form-control"
              value={formData.stock}
              onChange={handleChange}
              required
              min="0"
              placeholder="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="available_from" className="form-label">
              Date de disponibilité (optionnel)
            </label>
            <input
              type="datetime-local"
              id="available_from"
              name="available_from"
              className="form-control"
              value={formData.available_from}
              onChange={handleChange}
            />
            <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
              Laissez vide pour rendre le produit disponible immédiatement. Sinon, le produit sera disponible à partir de cette date.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">
              Images du produit (optionnel)
            </label>
            <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
              Vous pouvez ajouter plusieurs images. La première image sera l'image principale.
            </p>
            
            {images.length > 0 && (
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", 
                gap: "1rem", 
                marginBottom: "1rem" 
              }}>
                {images.map((image, index) => (
                  <div key={index} style={{ position: "relative" }}>
                    <img
                      src={image.preview || image.url}
                      alt={`Aperçu ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "150px",
                        objectFit: "cover",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--border-light)",
                      }}
                    />
                    <div style={{
                      position: "absolute",
                      top: "0.25rem",
                      right: "0.25rem",
                      display: "flex",
                      gap: "0.25rem",
                      flexDirection: "column"
                    }}>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, -1)}
                          className="btn btn-sm"
                          style={{
                            padding: "0.25rem",
                            minWidth: "auto",
                            fontSize: "0.75rem"
                          }}
                          title="Déplacer vers le haut"
                        >
                          <ChevronUp size={14} strokeWidth={2.5} />
                        </button>
                      )}
                      {index < images.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, 1)}
                          className="btn btn-sm"
                          style={{
                            padding: "0.25rem",
                            minWidth: "auto",
                            fontSize: "0.75rem"
                          }}
                          title="Déplacer vers le bas"
                        >
                          <ChevronDown size={14} strokeWidth={2.5} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="btn btn-error btn-sm"
                        style={{
                          padding: "0.25rem",
                          minWidth: "auto",
                          fontSize: "0.75rem"
                        }}
                        title="Supprimer"
                      >
                        <X size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                    {index === 0 && (
                      <div style={{
                        position: "absolute",
                        bottom: "0.25rem",
                        left: "0.25rem",
                        background: "var(--primary)",
                        color: "white",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "var(--radius-sm)",
                        fontSize: "0.75rem"
                      }}>
                        Principale
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-outline"
                disabled={uploadingImage}
              >
                {uploadingImage ? "Upload..." : (<><FolderOpen size={16} strokeWidth={2.25} /> Ajouter des images</>)}
              </button>
              
              <button
                type="button"
                onClick={handleTakePhoto}
                className="btn btn-outline"
                disabled={uploadingImage}
              >
                <Camera size={16} strokeWidth={2.25} /> Prendre une photo
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                id="is_available"
                name="is_available"
                checked={formData.is_available}
                onChange={handleChange}
              />
              <span>Produit disponible</span>
            </label>
            <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
              Décochez pour masquer temporairement ce produit
            </p>
          </div>

          <div style={{ display: "flex", gap: "1rem" }}>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={saving || success}
              style={{ flex: 1 }}
            >
              {saving ? "Enregistrement..." : "Enregistrer les modifications"}
            </button>
            <Link
              to="/my-products"
              className="btn btn-outline btn-lg"
            >
              Annuler
            </Link>
          </div>
        </form>
      </div>

      {/* Modal de la caméra */}
      {showCamera && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.9)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem"
          }}
        >
          <div style={{
            width: "100%",
            maxWidth: "600px",
            position: "relative",
            background: "#000",
            borderRadius: "var(--radius-md)",
            overflow: "hidden"
          }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: "100%",
                height: "auto",
                display: "block"
              }}
            />
            <canvas
              ref={canvasRef}
              style={{ display: "none" }}
            />
            
            <div style={{
              position: "absolute",
              bottom: "2rem",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "1rem",
              alignItems: "center"
            }}>
              <button
                type="button"
                onClick={closeCamera}
                className="btn btn-outline"
                style={{
                  background: "rgba(255, 255, 255, 0.9)",
                  color: "#000"
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={capturePhoto}
                className="btn btn-primary"
                style={{
                  width: "70px",
                  height: "70px",
                  borderRadius: "50%",
                  background: "#fff",
                  border: "4px solid #fff",
                  boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.3)"
                }}
                disabled={uploadingImage}
              >
                <Camera size={26} strokeWidth={2} color="#1a1a1a" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProduct;

